import React, { useEffect, Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChatProvider } from "./contexts/ChatContext";
import { useUserRole } from "./hooks/useUserRole";
import { usePermissions } from "./hooks/usePermissions";
import { setSecurityHeaders, removeSecurityHeaders } from "@/lib/security-headers";
import { isChurchSubdomain, isMarketingSite } from "@/utils/subdomain";
import { LoadingState } from "@/components/ui/loading-state";
import Layout from "./components/Layout";
import { directoryRoutes } from "./routes/directory.routes";
import ErrorBoundary from "./components/ErrorBoundary";
import WaitingForApproval from './components/WaitingForApproval';

// ---------------------------------------------------------------------------
// Every page is route-level code-split.
//
// These were previously static imports, which meant one monolithic bundle: a
// congregant opening the app downloaded every super-admin screen, every admin
// table and every settings page before seeing anything. Each page is now its
// own chunk, fetched on navigation. The <Routes> tree (and the two non-router
// branches below) are wrapped in Suspense boundaries to cover the load.
//
// Keep these as lazy() — adding a static page import here silently pulls that
// page (and its dependency tree) back into the initial bundle.
// ---------------------------------------------------------------------------

// Auth / account
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForcePasswordReset = lazy(() => import("./pages/ForcePasswordReset"));
const Profile = lazy(() => import("./pages/Profile"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));

// Member-facing
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Members = lazy(() => import("./pages/Members"));
const Chat = lazy(() => import("./pages/Chat"));
const ServiceManagement = lazy(() => import("./pages/ServiceManagement"));
const GuestManagement = lazy(() => import("./pages/GuestManagement"));
const CalendarView = lazy(() => import("./pages/CalendarView"));
const SMSMessaging = lazy(() => import("./pages/SMSMessaging"));
const PrayerWall = lazy(() => import("./pages/PrayerWall"));
const Attendance = lazy(() => import("./pages/Attendance"));
const Giving = lazy(() => import("./pages/Giving"));
const SmallGroups = lazy(() => import("./pages/SmallGroups"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Volunteers = lazy(() => import("./pages/Volunteers"));
const ChurchSettings = lazy(() => import("./pages/ChurchSettings"));

// Public / marketing
const MarketingLandingPage = lazy(() => import("./pages/MarketingLandingPage"));
const PublicChurch = lazy(() => import("./pages/PublicChurch"));
const PublicCalendar = lazy(() => import("./pages/PublicCalendar"));
const PublicBusinessDirectory = lazy(() => import("./pages/PublicBusinessDirectory"));
const ChurchPublicPage = lazy(() => import("./pages/public/ChurchPublicPage"));
const BusinessDirectoryPage = lazy(() => import("./pages/public/BusinessDirectoryPage"));
const PublicCalendarPage = lazy(() => import("./pages/public/PublicCalendarPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const SentryExamplePage = lazy(() => import("./pages/SentryExamplePage"));
const HealthCheck = lazy(() => import("./pages/HealthCheck"));
const StatusPage = lazy(() => import("./pages/StatusPage"));

// Admin — dashboard / reporting
const SuperAdminPage = lazy(() => import("./pages/admin/SuperAdminPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const ReportsPage = lazy(() => import("./pages/admin/ReportsPage"));
const LogsPage = lazy(() => import("./pages/admin/LogsPage"));

// Admin — people
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminChurchNew = lazy(() => import("./pages/admin/AdminChurchNew"));
const ChurchesPage = lazy(() => import("./pages/admin/ChurchesPage"));
const ChurchOverviewPage = lazy(() => import("./pages/admin/ChurchOverviewPage"));
const MembersPage = lazy(() => import("./pages/admin/MembersPage"));
const VolunteersPage = lazy(() => import("./pages/admin/VolunteersPage"));
const StaffPage = lazy(() => import("./pages/admin/StaffPage"));
const GroupsPage = lazy(() => import("./pages/admin/GroupsPage"));
const AttendancePage = lazy(() => import("./pages/admin/AttendancePage"));

// Admin — engagement
const PrayerWallPage = lazy(() => import("./pages/admin/PrayerWallPage"));
const EventsPage = lazy(() => import("./pages/admin/EventsPage"));
const MessagesPage = lazy(() => import("./pages/admin/MessagesPage"));
const PostsPage = lazy(() => import("./pages/admin/PostsPage"));

// Admin — operations
const CampusesPage = lazy(() => import("./pages/admin/CampusesPage"));
const TransportationPage = lazy(() => import("./pages/admin/TransportationPage"));
const SafetyPage = lazy(() => import("./pages/admin/SafetyPage"));
const MediaPage = lazy(() => import("./pages/admin/MediaPage"));
const SchedulesPage = lazy(() => import("./pages/admin/SchedulesPage"));

// Admin — finance
const GivingPage = lazy(() => import("./pages/admin/GivingPage"));
const ContributionsPage = lazy(() => import("./pages/admin/ContributionsPage"));
const ExpensesPage = lazy(() => import("./pages/admin/ExpensesPage"));
const BudgetPage = lazy(() => import("./pages/admin/BudgetPage"));

// Admin — settings
const IntegrationsPage = lazy(() => import("./pages/admin/IntegrationsPage"));
const PermissionsPage = lazy(() => import("./pages/admin/PermissionsPage"));
const AppearancePage = lazy(() => import("./pages/admin/AppearancePage"));
const NotificationsPage = lazy(() => import("./pages/admin/NotificationsPage"));
const AccountPage = lazy(() => import("./pages/admin/AccountPage"));
const SystemPage = lazy(() => import("./pages/admin/SystemPage"));
const AuditTrailPage = lazy(() => import("./pages/admin/AuditTrailPage"));

// Optimized QueryClient configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Prevent refetch storms
      retry: 1, // Reduce retries for faster failure handling
      retryDelay: 1000, // 1 second retry delay
    },
  },
});

// Unified route component that shows the super-admin home (SuperAdminPage) for super admins, regular dashboard for others
const UnifiedRoute = () => {
  const { user, loading, passwordResetRequired } = useAuth();
  
  // useUserRole wraps react-query and does not throw during render, so it must be
  // called unconditionally (React hooks cannot be inside try/catch). Defensive
  // defaults are still applied below.
  const roleResult = useUserRole();

  const { isSuperAdmin = false, loading: roleLoading = true, roles = [] } = roleResult || {};
  
  // Add additional safety check
  const safeRoleLoading = typeof roleLoading === 'boolean' ? roleLoading : true;
  const safeIsSuperAdmin = typeof isSuperAdmin === 'boolean' ? isSuperAdmin : false;
  
  if (import.meta.env.DEV) {
    console.debug('🔍 UnifiedRoute - State check:', {
      userLoading: loading,
      roleLoading: safeRoleLoading,
      isSuperAdmin: safeIsSuperAdmin,
      hasUser: !!user,
      passwordResetRequired
    });
  }
  
  if (loading || safeRoleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If user needs to reset password, redirect to force password reset page
  if (passwordResetRequired) {
    return <Navigate to="/force-password-reset" replace />;
  }
  // Show the super-admin home (SuperAdminPage) for super admins
  if (safeIsSuperAdmin) {
    if (import.meta.env.DEV) {
      console.debug('✅ Rendering SuperAdminPage');
    }
    return (
      <Layout>
        <Suspense fallback={<LoadingState fullPage message="Loading dashboard..." />}>
          <SuperAdminPage />
        </Suspense>
      </Layout>
    );
  }
  
  // For regular users, show the regular dashboard
  return (
    <Layout>
      <Suspense fallback={<LoadingState fullPage message="Loading dashboard..." />}>
        <Dashboard />
      </Suspense>
    </Layout>
  );
};

// Protected Route component for Members
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, passwordResetRequired } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If user needs to reset password, redirect to force password reset page
  if (passwordResetRequired) {
    return <Navigate to="/force-password-reset" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Shared loading spinner for route guards.
const RouteSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Church-management routes (super admin OR church admin), in the unified shell.
// For /church/:churchId/* a church admin must administer THAT church; non-scoped
// (legacy) routes allow any church admin.
const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { isChurchAdmin, administersChurch, loading: permsLoading } = usePermissions();
  const { churchId } = useParams();

  if (loading || permsLoading) return <RouteSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isChurchAdmin) return <Navigate to="/" replace />;

  // Per-church scoping: a church admin may only manage churches they administer.
  if (churchId && !administersChurch(churchId)) return <Navigate to="/" replace />;

  return <Layout>{children}</Layout>;
};

// Genuinely super-admin-only (platform-level) routes, in the unified shell.
const SuperAdminOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { isSuperAdmin, loading: permsLoading } = usePermissions();

  if (loading || permsLoading) return <RouteSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return <Layout>{children}</Layout>;
};

// Church-admin features that live in the member/admin shell (church admin OR
// super admin). Enforces the role at the router, not just by hiding the menu.
const ChurchAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, passwordResetRequired } = useAuth();
  const { isChurchAdmin, loading: permsLoading } = usePermissions();

  if (loading || permsLoading) return <RouteSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (passwordResetRequired) return <Navigate to="/force-password-reset" replace />;
  if (!isChurchAdmin) return <Navigate to="/" replace />;

  return <Layout>{children}</Layout>;
};

const App = () => {
  // Ensure body is visible (pure SPA - no hydration needed)
  useEffect(() => {
    document.body.style.opacity = '1';
    document.body.style.visibility = 'visible';
  }, []);

  useEffect(() => {
    // Apply security headers based on current route
    const updateSecurityHeaders = () => {
      const currentPath = window.location.pathname;
      
      // Apply restrictive security headers for admin and auth routes
      if (currentPath.startsWith('/admin') || currentPath.startsWith('/auth')) {
        setSecurityHeaders();
      } else {
        // Remove restrictive headers for public routes
        removeSecurityHeaders();
      }
    };

    // Set initial headers
    updateSecurityHeaders();
    
    // Listen for route changes to update security headers
    const handleRouteChange = () => {
      setTimeout(updateSecurityHeaders, 0); // Defer to ensure route has changed
    };
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Check domain type and routing
  const isChurchSite = isChurchSubdomain();
  const testChurch = new URLSearchParams(window.location.search).get('church');
  const isMarketing = isMarketingSite();

  // If it's churchhub.us (marketing site), show the marketing landing page
  if (isMarketing) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ErrorBoundary>
            <Suspense fallback={<LoadingState fullPage message="Loading..." />}>
              <MarketingLandingPage />
            </Suspense>
          </ErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  // If it's a church subdomain or has church test parameter, show the public church page
  if (isChurchSite || testChurch) {
    if (import.meta.env.DEV) {
      console.debug('⛪ Rendering PublicChurch page');
    }
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ErrorBoundary>
            <Suspense fallback={<LoadingState fullPage message="Loading..." />}>
              <PublicChurch />
            </Suspense>
          </ErrorBoundary>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  if (import.meta.env.DEV) {
    console.debug('🏠 Rendering regular app with routing');
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
              {/* ChatProvider is mounted only on the /chat route (below), not
                  app-wide. It connects the Stream client on mount and
                  disconnects on unmount, so a member who never opens chat is
                  never counted against Stream's MAU / concurrent-connection
                  limits. See docs/adr/0002. */}
              {/* Single boundary covering every lazily-loaded route. */}
              <Suspense fallback={<LoadingState fullPage message="Loading..." />}>
              <Routes>
                {/* ========== PUBLIC ROUTES ========== */}
                <Route path="/login" element={<Auth />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/calendar" element={<PublicCalendar />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/v1/verify" element={<ResetPassword />} />
                <Route path="/force-password-reset" element={<ForcePasswordReset />} />
                <Route path="/api/integrations/google/callback" element={<GoogleCallback />} />
                
                {/* Health Check - Public endpoint for monitoring */}
                <Route 
                  path="/health" 
                  element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <HealthCheck />
                    </Suspense>
                  } 
                />
                
                {/* Status Page - Public status page */}
                <Route 
                  path="/status" 
                  element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <StatusPage />
                    </Suspense>
                  } 
                />
                
                {/* Public Pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/sentry-example-page" element={<SentryExamplePage />} />
                
                {/* Public Church Pages */}
                <Route path="/:churchSlug" element={<ChurchPublicPage />} />
                <Route path="/:churchSlug/business" element={<BusinessDirectoryPage />} />
                <Route path="/:churchSlug/calendar" element={<PublicCalendarPage />} />
                <Route path="/:churchSlug/business-legacy" element={<PublicBusinessDirectory />} />

                {/* ========== DASHBOARD - AUTO-DETECTS ROLE ========== */}
                <Route path="/" element={<UnifiedRoute />} />

                {/* ========== MEMBER ROUTES (Protected) ========== */}
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/calendar-view" element={<ProtectedRoute><CalendarView /></ProtectedRoute>} />
                <Route path="/prayers" element={<ProtectedRoute><PrayerWall /></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><ChatProvider><Chat /></ChatProvider></ProtectedRoute>} />
                <Route path="/small-groups" element={<ProtectedRoute><SmallGroups /></ProtectedRoute>} />
                <Route path="/public-business-directory" element={<ProtectedRoute><PublicBusinessDirectory /></ProtectedRoute>} />
                <Route path="/waiting-approval" element={<ProtectedRoute><WaitingForApproval /></ProtectedRoute>} />
                
                {/* Directory Routes - Member accessible */}
                {directoryRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={
                      <ProtectedRoute>
                        {route.element}
                      </ProtectedRoute>
                    }
                  />
                ))}

                {/* ========== ADMIN ROUTES (Church Admin & Super Admin) ========== */}
                {/* These could be accessed by church admins managing their own church */}
                {/* Clearly admin-only — restricted to church admins / super admins
                    at the router (previously only hidden from the menu). */}
                <Route path="/members" element={<ChurchAdminRoute><Members /></ChurchAdminRoute>} />
                <Route path="/guests" element={<ChurchAdminRoute><GuestManagement /></ChurchAdminRoute>} />
                <Route path="/sms" element={<ChurchAdminRoute><SMSMessaging /></ChurchAdminRoute>} />
                <Route path="/analytics" element={<ChurchAdminRoute><Analytics /></ChurchAdminRoute>} />
                <Route path="/settings" element={<ChurchAdminRoute><ChurchSettings /></ChurchAdminRoute>} />
                {/* TODO(Phase 2): classify member-vs-admin access before restricting —
                    these may be member-facing (view services, own attendance,
                    volunteer signup, member giving). Left as authenticated-only. */}
                <Route path="/services" element={<ProtectedRoute><ServiceManagement /></ProtectedRoute>} />
                <Route path="/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
                <Route path="/volunteers" element={<ProtectedRoute><Volunteers /></ProtectedRoute>} />
                <Route path="/giving" element={<ProtectedRoute><Giving /></ProtectedRoute>} />

                {/* Church-specific routes (for multi-church super admin view) */}
                <Route path="/church/:churchId" element={<SuperAdminRoute><ChurchOverviewPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/members" element={<SuperAdminRoute><MembersPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/groups" element={<SuperAdminRoute><GroupsPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/volunteers" element={<SuperAdminRoute><VolunteersPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/events" element={<SuperAdminRoute><EventsPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/attendance" element={<SuperAdminRoute><AttendancePage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/giving" element={<SuperAdminRoute><GivingPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/budgets" element={<SuperAdminRoute><BudgetPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/messages" element={<SuperAdminRoute><MessagesPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/prayer-wall" element={<SuperAdminRoute><PrayerWallPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/media" element={<SuperAdminRoute><MediaPage /></SuperAdminRoute>} />
                <Route path="/church/:churchId/settings" element={<SuperAdminRoute><ChurchSettings /></SuperAdminRoute>} />

                {/* ========== SUPER ADMIN ONLY ROUTES ========== */}
                <Route path="/churches/new" element={<SuperAdminOnlyRoute><AdminChurchNew /></SuperAdminOnlyRoute>} />
                <Route path="/superadmin/analytics" element={
                  <SuperAdminOnlyRoute>
                    <Suspense fallback={<LoadingState message="Loading analytics..." />}>
                      <AnalyticsPage />
                    </Suspense>
                  </SuperAdminOnlyRoute>
                } />
                <Route path="/superadmin/reports" element={
                  <SuperAdminOnlyRoute>
                    <Suspense fallback={<LoadingState message="Loading reports..." />}>
                      <ReportsPage />
                    </Suspense>
                  </SuperAdminOnlyRoute>
                } />
                <Route path="/superadmin/audit" element={<SuperAdminOnlyRoute><AuditTrailPage /></SuperAdminOnlyRoute>} />
                <Route path="/superadmin/integrations" element={
                  <SuperAdminOnlyRoute>
                    <Suspense fallback={<LoadingState message="Loading integrations..." />}>
                      <IntegrationsPage />
                    </Suspense>
                  </SuperAdminOnlyRoute>
                } />
                <Route path="/superadmin/users" element={
                  <SuperAdminOnlyRoute>
                    <Suspense fallback={<LoadingState message="Loading users..." />}>
                      <AdminUsers />
                    </Suspense>
                  </SuperAdminOnlyRoute>
                } />
                <Route path="/superadmin/settings" element={
                  <SuperAdminOnlyRoute>
                    <Suspense fallback={<LoadingState message="Loading settings..." />}>
                      <SystemPage />
                    </Suspense>
                  </SuperAdminOnlyRoute>
                } />

                {/* ========== LEGACY ADMIN ROUTES (backward compatibility) ========== */}
                <Route path="/churches" element={<SuperAdminRoute><ChurchesPage /></SuperAdminRoute>} />
                <Route path="/reports" element={<SuperAdminRoute><ReportsPage /></SuperAdminRoute>} />
                <Route path="/logs" element={<SuperAdminRoute><LogsPage /></SuperAdminRoute>} />
                <Route path="/staff" element={<SuperAdminRoute><StaffPage /></SuperAdminRoute>} />
                <Route path="/groups" element={<SuperAdminRoute><GroupsPage /></SuperAdminRoute>} />
                <Route path="/prayerwall" element={<SuperAdminRoute><PrayerWallPage /></SuperAdminRoute>} />
                <Route path="/events" element={<SuperAdminRoute><EventsPage /></SuperAdminRoute>} />
                <Route path="/messages" element={<SuperAdminRoute><MessagesPage /></SuperAdminRoute>} />
                <Route path="/posts" element={<SuperAdminRoute><PostsPage /></SuperAdminRoute>} />
                <Route path="/campuses" element={<SuperAdminRoute><CampusesPage /></SuperAdminRoute>} />
                <Route path="/transportation" element={<SuperAdminRoute><TransportationPage /></SuperAdminRoute>} />
                <Route path="/safety" element={<SuperAdminRoute><SafetyPage /></SuperAdminRoute>} />
                <Route path="/media" element={<SuperAdminRoute><MediaPage /></SuperAdminRoute>} />
                <Route path="/schedules" element={<SuperAdminRoute><SchedulesPage /></SuperAdminRoute>} />
                <Route path="/contributions" element={<SuperAdminRoute><ContributionsPage /></SuperAdminRoute>} />
                <Route path="/expenses" element={<SuperAdminRoute><ExpensesPage /></SuperAdminRoute>} />
                <Route path="/budgets" element={<SuperAdminRoute><BudgetPage /></SuperAdminRoute>} />
                <Route path="/integrations" element={<SuperAdminRoute><IntegrationsPage /></SuperAdminRoute>} />
                <Route path="/permissions" element={<SuperAdminRoute><PermissionsPage /></SuperAdminRoute>} />
                <Route path="/appearance" element={<SuperAdminRoute><AppearancePage /></SuperAdminRoute>} />
                <Route path="/notifications" element={<SuperAdminRoute><NotificationsPage /></SuperAdminRoute>} />
                <Route path="/account" element={<SuperAdminRoute><AccountPage /></SuperAdminRoute>} />
                <Route path="/system" element={<SuperAdminRoute><SystemPage /></SuperAdminRoute>} />
                <Route path="/system/audit" element={<SuperAdminRoute><AuditTrailPage /></SuperAdminRoute>} />
                
                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;